import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND } from "../data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/friday-plates", label: "Friday Plates" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/meal-prep", label: "Meal Prep" },
  { to: "/gallery", label: "Gallery" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/faq", label: "FAQ" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || location.pathname !== "/";

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? "bg-charcoal/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 sm:gap-3">
            <img
              src={BRAND.logo}
              alt="Taste of Platinum"
              className="w-10 h-10 lg:w-12 lg:h-12 rounded-full object-cover ring-1 ring-gold/40 shrink-0"
            />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-xl lg:text-2xl font-semibold tracking-wide text-white">
                Taste of <span className="gold-text">Platinum</span>
              </span>
              <span className="text-[9px] lg:text-[10px] tracking-[0.28em] text-platinum-dark uppercase mt-0.5">
                Island Flavor · Platinum Experience
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors ${
                    isActive ? "text-gold" : "text-platinum hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-quote-button"
              className="ml-2 inline-flex items-center rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-charcoal transition-transform hover:scale-[1.03] hover:bg-gold-bright"
            >
              Request a Quote
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-white hover:bg-white/10"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden bg-charcoal border-t border-white/10 transition-[max-height] duration-300 ${
          open ? "max-h-[560px]" : "max-h-0"
        }`}
      >
        <nav className="px-4 py-4 flex flex-col">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `py-3 px-2 text-base font-medium border-b border-white/5 ${
                  isActive ? "text-gold" : "text-platinum"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="mobile-nav-quote-button"
            className="mt-4 inline-flex items-center justify-center rounded-full bg-gold px-5 py-3 text-base font-semibold text-charcoal"
          >
            Request a Quote
          </Link>
          <a
            href={BRAND.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 text-center py-2 text-sm text-platinum-dark"
          >
            Follow @{BRAND.instagram}
          </a>
        </nav>
      </div>
    </header>
  );
}
